<template>
  <div class="onBoading">
    <div class="onBoading__top">
      <img
        src="@/assets/logo-monespace.png"
        alt="Onboarding"
      />
      <div class="onBoading__top__content">
        <BasicText
          size="h2"
          color="white"
          weight="bold"
        >
          👋🏻 Bienvenue sur Mon Espace
        </BasicText>
        <BasicText
          size="h5"
          color="white"
          weight="light"
        >
          Accédez à votre portail AKTO pour gérer toutes vos démarches administratives liées à la formation et aux
          contrats en alternance. Vous pouvez déposer vos demandes de financement, suivre l'avancement de vos demandes
          et consulter vos documents utiles, le tout dans un espace sécurisé et optimisé.
        </BasicText>
        <div class="onBoading__top__content--button">
          <BasicButton
            label="Se connecter"
            size="large"
            @click="appState.connexionAsync"
            style="width: 400px"
          />
        </div>
      </div>
    </div>
    <div class="onBoading__middle">
      <div class="onBoading__middle__information">
        <div class="onBoading__middle__information__header">
          <BasicIcon
            name="info-circle"
            active
            color="primary-600"
          />
          <BasicText
            size="h5"
            color="secondary-800"
          >
            Informations
          </BasicText>
        </div>
        <BasicAlert alertType="warning">
          <template #label>
            <BasicText color="secondary-800">
              Compte tenu du nombre de dossiers déjà validés ou actuellement en cours d’instruction, le dépôt des demandes PIC IAE 2025 est clos depuis le 23 novembre 2025
            </BasicText>
          </template>
        </BasicAlert>
        <BasicAlert alertType="warning">
          <template #label>
            <BasicText color="secondary-800">
              La date limite de réception des dossiers est fixée au <BasicText color="secondary-800" weight="bold">23 novembre 2025</BasicText> pour être pris en compte sur les budgets 2025.
              <br />
              Sont concernées :
              <br />
              <ul>
                <li><BasicText color="secondary-800">les formations terminées avant le 31 décembre 2025,</BasicText></li>
                <li><BasicText color="secondary-800">les formations commencées en 2025 et achevées en 2026 ou 2027 (formations certifiantes)</BasicText></li>
                <li><BasicText color="secondary-800">et, à titre exceptionnel, les formations débutant avant le 15 janvier 2026.</BasicText></li>
              </ul>
            </BasicText>
          </template>
        </BasicAlert>
        <BasicAlert alertType="info">
          <template #label>
            <BasicText color="secondary-800">
              Financement de vos formations en 2025 : Consultez les
              <BasicLink
                label="règles de prise en charge"
                @link-click="redirectExterne('https://www.akto.fr/financer-une-formation/regles-de-prise-en-charge/')"
              />
              définies par votre branche professionnelle.
            </BasicText>
          </template>
        </BasicAlert>
        <BasicText color="secondary-800">
          <BasicText weight="bold">Mon Espace</BasicText>
          , votre portail AKTO dédié à la gestion administrative de vos dossiers de formation et/ou contrats en
          alternance, évolue pour vous apporter un meilleur service et plus de fluidité dans vos demandes de financement
          et leur suivi.
        </BasicText>
        <BasicText
          color="secondary-800"
          weight="bold"
        >
          Accédez à votre nouvel espace :
        </BasicText>
        <BasicText color="secondary-800">
          Pour profiter de ces améliorations, il est nécessaire de recréer un compte sur notre nouveau portail avec
          votre email actuel. Ce processus est simple et rapide, et nous vous avons préparé un
          <BasicText color="secondary-800">
            <BasicLink
              label="tutoriel détaillé"
              @link-click="redirectExterne('https://s.akto.fr/EJCF4N')"
            />
          </BasicText>
          pour vous guider pas à pas
        </BasicText>
        <BasicText
          color="secondary-800"
          weight="bold"
        >
          Vous avez besoin d'aide pour accéder à Mon Espace ?
        </BasicText>
        <BasicText color="secondary-800">
          Nos équipes sont à votre disposition pour vous accompagner à chaque étape. N'hésitez pas à nous contacter via
          notre
          <BasicLink
            label="formulaire de contact"
            @link-click="
              redirectExterne(
                'https://www.akto.fr/nous-contacter/entreprises/#vous-n-avez-pas-trouve-votre-reponse-n-hesitez-pas-a-nous-contacter',
              )
            "
          />
        </BasicText>
      </div>
      <div class="onBoading__middle__actualite">
        <cards-layout
          cards-type="Actualités"
          headerText="Agenda"
          :cards="cardsListAgendas"
          :redirection="() => redirectExterne('https://www.akto.fr/agenda/')"
        />
      </div>
    </div>
    <div class="onBoading__bottom">
      <div class="onBoading__bottom__header">
        <BasicText
          size="h5"
          color="secondary-800"
        >
          Articles
        </BasicText>
        <BasicLink
          label="Tout voir"
          @link-click="redirectExterne('https://www.akto.fr/articles/')"
        />
      </div>
      <div class="onBoading__bottom__articles">
        <div
          class="onBoading__bottom__articles--cards"
          v-for="actu in actualites"
          :key="actu.url!"
        >
          <BasicText
            color="secondary-800"
            weight="bold"
          >
            {{ dateFormatter(actu.date!) }}
          </BasicText>
          <BasicText
            color="secondary-800"
            weight="bold"
            size="body-xl"
          >
            <BasicLink
              :label="actu.title!"
              @link-click="redirectExterne(actu.url!)"
            />
          </BasicText>
          <BasicText
            color="dark-jungle-green"
            size="body-m"
          >
            {{ truncateEllipsis(actu.text!, 200) }}
          </BasicText>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useAppStateStore } from '@/features/application'
import CardsLayout from '@/features/interface/home/components/cardsLayout/CardsLayout.vue'
import { dateFormatter, truncateEllipsis } from '@/features/shared/tools'
import { redirectExterne } from '@/features/shared/tools/url'
import { storeToRefs } from 'pinia'
import { useOnboardingStore } from './useOnboardingStore'

  const appState = useAppStateStore()
  const onboardingStore = useOnboardingStore()
  const { actualites, cardsListAgendas } = storeToRefs(onboardingStore)
</script>

<style scoped lang="less">
  @import './OnboardingView.less';
</style>

